import { useEffect, useState } from "react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, Trash2, ShieldCheck, Mail, Copy, X, Link as LinkIcon, Send,
  Eye, Pencil, Camera, Save, MailCheck, MailWarning, Download, Undo2,
} from "lucide-react";
import { fmtDate } from "@/lib/format";
import CertBadges from "@/components/advisory/CertBadges";
import TrainingBoard from "@/components/advisory/TrainingBoard";

// Short "Last seen" pretty-printer. Returns "Just now / 5m ago / 3h ago /
// 2d ago / 3w ago / MM/DD/YYYY" based on age. Falls back to "Never" when no
// login has been recorded yet (legacy accounts).
function fmtRelative(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "Just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  if (sec < 2592000) return `${Math.floor(sec / 604800)}w ago`;
  return fmtDate(iso);
}

// Client-side downscale: reads a File, draws onto a canvas of `size`x`size`
// preserving aspect (center-cropped), and returns a JPEG data URL ~quality 0.85.
// Keeps the avatar payload tiny (well under the 700KB backend cap).
async function downscaleImage(file, size = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        // Center-crop the longest dim to a square, then scale down to `size`.
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function colorFromString(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 50% 45%)`;
}

function Avatar({ user, size = 32 }) {
  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  if (user?.avatar_base64) {
    return (
      <img
        src={user.avatar_base64}
        alt={user.name}
        data-testid={`user-avatar-${user.id}`}
        className="object-cover rounded-full shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="grid place-items-center text-white font-medium shrink-0 rounded-full"
      style={{ width: size, height: size, background: colorFromString(user?.email || user?.name || ""), fontSize: size * 0.4 }}
      data-testid={`user-avatar-${user.id}`}
    >
      {initials}
    </div>
  );
}

const ROLES = [
  { v: "admin", label: "Admin", desc: "Full workspace access except billing" },
  { v: "accountant", label: "Accountant", desc: "Manage bank accounts, statements, categorize" },
  { v: "viewer", label: "Viewer", desc: "Read-only access to dashboards and reports" },
  { v: "client", label: "Client", desc: "External client — upload statements only (uses client portal)" },
];

export default function TeamUsers() {
  const { user: me, refreshMe } = useAuth();
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState({});
  const [tab, setTab] = useState("members");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "accountant" });
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "accountant" });
  const [lastInviteLink, setLastInviteLink] = useState("");

  const canManage = me && (me.role === "owner" || me.role === "admin");

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/users")];
      if (canManage) calls.push(api.get("/invites"));
      const results = await Promise.all(calls);
      setItems(results[0].data);
      if (canManage) setInvites(results[1].data);
      api.get("/training/certifications").then((r) => setCerts(r.data)).catch(() => {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [canManage]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "accountant" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/invites", inviteForm);
      const link = `${window.location.origin}/invite/${res.data.token}`;
      setLastInviteLink(link);
      toast.success(`Invite email sent to ${res.data.email}`);
      setInviteForm({ name: "", email: "", role: "accountant" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create invite");
    }
  };

  const resendInvite = async (id) => {
    try {
      const res = await api.post(`/invites/${id}/resend`);
      toast.success(`Invite email re-sent (${res.data.email_sent_count} total)`);
      // Give the background task a beat to stamp email_sent_at, then refresh.
      setTimeout(load, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend invite");
    }
  };

  const copyInvite = async (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied to clipboard");
    } catch {
      // Fallback
      const el = document.createElement("textarea");
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
      toast.success("Invite link copied");
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    const snap = items.find((u) => u.id === id);
    if (!snap) return;
    if (!window.confirm(`Remove ${snap.name || snap.email}? You can undo this for 10 seconds.`)) return;
    try {
      const { data } = await api.delete(`/users/${id}`);
      load();
      toast.success("User removed", {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/users/bulk-restore", { users: [snap] });
              toast.success(`Restored ${snap.name || snap.email}`);
              load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not restore");
            }
          },
        },
      });
      maybeOfferReclaim(data?.reclaim);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  // Bulk-selection state. Owners and the current user cannot be checked,
  // so the "select all" header only toggles deletable rows.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleOne = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const [bulkBusy, setBulkBusy] = useState(false);

  // --- CSV export (selected only) with column chooser ---
  // Available columns and their row-value accessors. Admins pick a subset;
  // the chosen set is persisted in localStorage so firms don't have to
  // re-pick every time to match their internal HR template.
  const CSV_COLUMNS = [
    { id: "name",       label: "Name",       get: (u) => u.name },
    { id: "email",      label: "Email",      get: (u) => u.email },
    { id: "role",       label: "Role",       get: (u) => u.role },
    { id: "status",     label: "Status",     get: (u) => (u.is_active === false ? "Inactive" : "Active") },
    { id: "created",    label: "Created",    get: (u) => u.created_at || "" },
    { id: "last_login", label: "Last login", get: (u) => u.last_login_at || "" },
    { id: "user_id",    label: "User ID",    get: (u) => u.id },
  ];
  const DEFAULT_COLS = ["name", "email", "role", "status", "created", "last_login"];
  const csvStorageKey = "ll:team:csv-columns";
  const [csvCols, setCsvCols] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(csvStorageKey) || "null");
      if (Array.isArray(saved) && saved.length > 0) return saved;
    } catch { /* ignore */ }
    return DEFAULT_COLS;
  });
  const [csvChooserOpen, setCsvChooserOpen] = useState(false);
  const toggleCsvCol = (id) => {
    setCsvCols((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { window.localStorage.setItem(csvStorageKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const bulkExportCsv = () => {
    if (selectedIds.size === 0) return;
    if (csvCols.length === 0) {
      toast.error("Pick at least one column to export");
      return;
    }
    const chosen = CSV_COLUMNS.filter((c) => csvCols.includes(c.id));
    const selected = items.filter((u) => selectedIds.has(u.id));
    const header = chosen.map((c) => c.label);
    const rows = selected.map((u) => chosen.map((c) => c.get(u)));
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const stamp = new Date().toISOString().slice(0, 10);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `team-members-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCsvChooserOpen(false);
    toast.success(`Exported ${selected.length} user${selected.length === 1 ? "" : "s"} · ${chosen.length} col${chosen.length === 1 ? "" : "s"}`);
  };

  // --- Bulk role change ---
  const bulkRoleChange = async (newRole) => {
    if (selectedIds.size === 0 || !newRole) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.patch(`/users/${id}`, { role: newRole }); ok++; }
      catch { fail++; }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    load();
    if (ok > 0) toast.success(`Updated ${ok} user${ok === 1 ? "" : "s"} to ${newRole}${fail ? ` · ${fail} failed` : ""}`);
    else if (fail > 0) toast.error(`Failed to update ${fail} user${fail === 1 ? "" : "s"}`);
  };

  // --- Bulk delete with 10s Undo ---
  // We snapshot the full user docs BEFORE issuing the DELETE calls so an
  // Undo tap can hand them back verbatim to /users/bulk-restore. Keeping
  // the snapshot client-side (vs. a soft-delete column) means a hard
  // refresh after Undo expiry won't resurrect anyone unexpectedly.
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedIds.size} selected user${selectedIds.size === 1 ? "" : "s"}? You can undo this for 10 seconds.`)) return;
    const snapshots = items.filter((u) => selectedIds.has(u.id));
    const ids = snapshots.map((u) => u.id);
    setBulkBusy(true);
    let ok = 0, fail = 0;
    let lastReclaim = null;
    for (const id of ids) {
      try {
        const { data } = await api.delete(`/users/${id}`);
        ok++;
        if (data?.reclaim?.unused > 0) lastReclaim = data.reclaim;
      } catch { fail++; }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    load();
    if (ok > 0) {
      // Fire-and-forget toast with Undo. The toast's own 10s duration is
      // the restore window — tap triggers bulk-restore from the snapshot.
      toast.success(`Removed ${ok} user${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post("/users/bulk-restore", { users: snapshots });
              toast.success(`Restored ${data?.restored ?? snapshots.length} user${(data?.restored ?? snapshots.length) === 1 ? "" : "s"}`);
              load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not restore");
            }
          },
        },
      });
    } else if (fail > 0) {
      toast.error(`Failed to remove ${fail} user${fail === 1 ? "" : "s"}`);
    }
    if (lastReclaim) maybeOfferReclaim(lastReclaim);
  };

  // View / Edit drawer state
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerMode, setDrawerMode] = useState("view"); // "view" | "edit"
  const [editForm, setEditForm] = useState({ name: "", role: "", default_bank_account_id: "" });
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        const res = await api.get("/bank-accounts");
        setBankAccounts(res.data);
      } catch { /* ignore */ }
    })();
  }, [canManage]);

  const openView = (u) => {
    setDrawerUser(u);
    setDrawerMode("view");
  };
  const openEdit = (u) => {
    setDrawerUser(u);
    setDrawerMode("edit");
    setEditForm({
      name: u.name,
      role: u.role,
      default_bank_account_id: u.default_bank_account_id || "",
    });
  };
  const saveEdit = async () => {
    try {
      // Only send default_bank_account_id if the user is a client — the field
      // is meaningless (and confusing) for staff roles.
      const payload = { name: editForm.name, role: editForm.role };
      if (editForm.role === "client") {
        payload.default_bank_account_id = editForm.default_bank_account_id || "";
      }
      await api.patch(`/users/${drawerUser.id}`, payload);
      toast.success("User updated");
      setDrawerUser(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "User marked inactive" : "User activated");
      load();
      // After a deactivation, check for unused paid team-member slots so
      // the firm can drop one from their monthly bill. Activating never
      // frees a slot so we skip the check there.
      if (u.is_active) {
        try {
          const { data } = await api.get("/billing/unused-slots");
          maybeOfferReclaim(data?.team_members);
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Reclaim prompt state — triggered after deactivation / deletion when the
  // backend reports unused paid add-on seats. One $10/mo seat at a time;
  // the persistent Settings card picks up the rest.
  const [reclaimOffer, setReclaimOffer] = useState(null); // { unused, monthly_savings_per_slot }
  const maybeOfferReclaim = (hint) => {
    if (!hint) return;
    const unused = Number(hint.unused || 0);
    if (unused > 0) setReclaimOffer(hint);
  };
  const confirmReclaim = async () => {
    try {
      const { data } = await api.post("/billing/team-members/reclaim");
      const saved = Number(data?.monthly_savings || 0);
      toast.success(saved > 0 ? `Reclaimed 1 seat — saving $${saved.toFixed(2)}/mo` : "Reclaimed 1 seat");
      await refreshMe?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reclaim seat");
    } finally {
      setReclaimOffer(null);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Image too large (max 2.5 MB)"); return; }
    // Downscale client-side to 256px square so the base64 payload stays well
    // under the 700KB backend cap regardless of source size.
    const dataUrl = await downscaleImage(file, 256);
    try {
      await api.patch("/users/me/avatar", { avatar_base64: dataUrl });
      toast.success("Profile picture updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const removeAvatar = async () => {
    try {
      await api.patch("/users/me/avatar", { avatar_base64: null });
      toast.success("Profile picture removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const pending = invites.filter((i) => i.status === "pending");

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Workspace users"
        testid="team-header"
        actions={canManage && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setInviteOpen(true); setLastInviteLink(""); }} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5" style={{ borderColor: "var(--ll-border)" }} data-testid="invite-user-btn">
              <Mail size={14} /> Invite by email
            </button>
            <button onClick={() => setOpen(true)} className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5" data-testid="add-user-btn">
              <Plus size={14} /> Add user
            </button>
          </div>
        )}
      />
      <PageBody>
        {canManage && (
          <div className="inline-flex border mb-5 bg-white" style={{ borderColor: "var(--ll-border)" }} data-testid="team-tabs">
            {[["members", "Members"], ["training", "Training"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`team-tab-${v}`}
                className={`px-4 py-2 text-sm transition-colors ${tab === v ? "bg-[var(--ll-ink)] text-white" : "hover:bg-[var(--ll-mint)]"}`}>{l}</button>
            ))}
          </div>
        )}
        {tab === "training" ? <TrainingBoard /> : (<>
        {loading && <div className="ll-progress mb-6" />}

        {/* Active users table */}
        <div className="ll-card-flat overflow-hidden mb-6">
          <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--ll-border)" }}>
            <div className="font-display font-bold text-lg">Active members</div>
            <div className="flex items-center gap-3">
              {canManage && selectedIds.size > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="bulk-actions-bar">
                  <span className="text-xs text-[var(--ll-ink-soft)]">
                    {selectedIds.size} selected
                  </span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCsvChooserOpen((v) => !v)}
                      disabled={bulkBusy}
                      data-testid="bulk-export-btn"
                      className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-row-hover)] inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      <Download size={12} />
                      Export CSV
                    </button>
                    {csvChooserOpen && (
                      <div
                        className="absolute right-0 top-full mt-1 z-20 bg-white border shadow-md p-3 w-64"
                        style={{ borderColor: "var(--ll-border)" }}
                        data-testid="csv-chooser"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">
                          Columns to include
                        </div>
                        <div className="space-y-1.5 mb-3">
                          {CSV_COLUMNS.map((c) => (
                            <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`csv-col-${c.id}`}>
                              <input
                                type="checkbox"
                                checked={csvCols.includes(c.id)}
                                onChange={() => toggleCsvCol(c.id)}
                                className="cursor-pointer"
                              />
                              <span>{c.label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setCsvChooserOpen(false)}
                            className="flex-1 text-xs px-3 py-1.5 border hover:bg-[var(--ll-row-hover)]"
                            style={{ borderColor: "var(--ll-border)" }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={bulkExportCsv}
                            disabled={csvCols.length === 0}
                            data-testid="csv-chooser-download"
                            className="flex-1 text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] disabled:opacity-60"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <select
                    onChange={(e) => {
                      const v = e.target.value;
                      e.target.value = "";
                      if (v) bulkRoleChange(v);
                    }}
                    disabled={bulkBusy}
                    data-testid="bulk-role-select"
                    defaultValue=""
                    className="text-xs px-2 py-1.5 border bg-white disabled:opacity-60"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="" disabled>Change role…</option>
                    <option value="admin">Admin</option>
                    <option value="accountant">Accountant</option>
                    <option value="viewer">Viewer</option>
                    <option value="client">Client</option>
                  </select>
                  <button
                    type="button"
                    onClick={bulkDelete}
                    disabled={bulkBusy}
                    data-testid="bulk-delete-btn"
                    className="text-xs px-3 py-1.5 bg-[var(--ll-error)] text-white hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    <Trash2 size={12} />
                    {bulkBusy ? "Working…" : `Delete ${selectedIds.size}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={bulkBusy}
                    data-testid="bulk-clear-btn"
                    className="text-xs px-2 py-1.5 border hover:bg-[var(--ll-bg)] disabled:opacity-60"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    Clear
                  </button>
                </div>
              )}
              <div className="text-xs text-[var(--ll-ink-soft)]">{items.length} total</div>
            </div>
          </div>
          <table className="w-full text-sm" data-testid="team-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white border-b" style={{ borderColor: "var(--ll-border)", background: "#0b5d9e" }}>
                {canManage && (
                  <th className="pl-5 pr-2 py-3 font-medium w-9">
                    {(() => {
                      const selectableIds = items.filter((u) => u.role !== "owner" && u.id !== me?.id).map((u) => u.id);
                      const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
                      return (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => {
                            setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
                          }}
                          data-testid="bulk-select-all"
                          aria-label="Select all removable users"
                          className="cursor-pointer accent-white"
                        />
                      );
                    })()}
                  </th>
                )}
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isMe = u.id === me?.id;
                const canSelect = canManage && u.role !== "owner" && !isMe;
                return (
                  <tr key={u.id} className="border-b hover:bg-[var(--ll-row-hover)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`user-row-${u.id}`}>
                    {canManage && (
                      <td className="pl-5 pr-2 py-3 w-9">
                        {canSelect && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(u.id)}
                            onChange={() => toggleOne(u.id)}
                            data-testid={`user-select-${u.id}`}
                            aria-label={`Select ${u.name}`}
                            className="cursor-pointer"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar user={u} size={32} />
                        <div className="min-w-0">
                          <div className="font-medium leading-tight">{u.name}{isMe && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">· you</span>}</div>
                          <div
                            className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-0.5"
                            data-testid={`user-last-seen-${u.id}`}
                            title={u.last_login_at ? `Last login: ${fmtDate(u.last_login_at)}` : "No login on record"}
                          >
                            Last seen · {fmtRelative(u.last_login_at)}
                          </div>
                          <CertBadges certs={certs[u.id]} userId={u.id} downloadFor={canManage || isMe ? u.id : null} name={u.name} />
                          {isMe && (
                            <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1 flex items-center gap-2">
                              <label className="cursor-pointer hover:text-[var(--ll-ink)] inline-flex items-center gap-1" data-testid="my-avatar-upload-label">
                                <Camera size={9} /> Change picture
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="my-avatar-upload-input" />
                              </label>
                              {u.avatar_base64 && (
                                <button type="button" onClick={removeAvatar} className="hover:text-[var(--ll-error)] inline-flex items-center gap-1" data-testid="my-avatar-remove">
                                  <X size={9} /> Remove
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[var(--ll-ink-soft)]">{u.email}</td>
                    <td className="px-5 py-3 text-xs uppercase tracking-widest">{u.role}</td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => canManage && u.role !== "owner" && !isMe && toggleStatus(u)}
                        disabled={!canManage || u.role === "owner" || isMe}
                        data-testid={`user-status-${u.id}`}
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 transition-colors ${
                          u.is_active
                            ? "bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0]"
                            : "bg-[#FEE2E2] text-[#991B1B] hover:bg-[#FECACA]"
                        } disabled:opacity-60 disabled:cursor-default`}
                        title={!canManage || u.role === "owner" || isMe ? "Cannot change this user's status" : "Toggle active/inactive"}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? "bg-[#10B981]" : "bg-[#DC2626]"}`} />
                        {u.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openView(u)} className="p-1.5 hover:bg-[var(--ll-mint)]" data-testid={`user-view-${u.id}`} title="View"><Eye size={14} /></button>
                        {canManage && u.role !== "owner" && (
                          <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-[var(--ll-mint)]" data-testid={`user-edit-${u.id}`} title="Edit"><Pencil size={14} /></button>
                        )}
                        {canManage && u.role !== "owner" && !isMe && (
                          <button onClick={() => del(u.id)} className="p-1.5 hover:bg-[var(--ll-error)] hover:text-white" data-testid={`user-delete-${u.id}`} title="Delete"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pending invites */}
        {canManage && (
          <div className="ll-card-flat overflow-hidden" data-testid="pending-invites-card">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
              <div className="font-display font-bold text-lg flex items-center gap-2">
                <LinkIcon size={16} className="opacity-60" /> Pending invites
              </div>
              <div className="text-xs text-[var(--ll-ink-soft)]">{pending.length} pending</div>
            </div>
            {pending.length === 0 ? (
              <div className="p-8 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="no-pending-invites">
                No pending invites. Click <span className="font-medium">Invite link</span> to generate one.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="pending-invites-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-white border-b" style={{ borderColor: "var(--ll-border)", background: "#0b5d9e" }}>
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">Role</th>
                    <th className="px-5 py-3 font-medium">Created</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((i) => (
                    <tr key={i.id} className="border-b" style={{ borderColor: "var(--ll-border)" }} data-testid={`invite-row-${i.id}`}>
                      <td className="px-5 py-3">{i.name}</td>
                      <td className="px-5 py-3 text-[var(--ll-ink-soft)]">{i.email}</td>
                      <td className="px-5 py-3 text-xs uppercase tracking-widest">{i.role}</td>
                      <td className="px-5 py-3 text-[var(--ll-ink-soft)] text-xs">{fmtDate(i.created_at)}</td>
                      <td className="px-5 py-3 text-xs" data-testid={`invite-delivery-${i.id}`}>
                        {i.last_email_error ? (
                          <span className="inline-flex items-center gap-1 text-[#991B1B]" title={i.last_email_error}>
                            <MailWarning size={12} /> Failed
                          </span>
                        ) : i.email_sent_at ? (
                          <span className="inline-flex items-center gap-1 text-[#065F46]" title={`Sent ${fmtRelative(i.email_sent_at)}`}>
                            <MailCheck size={12} /> Sent {(i.email_sent_count || 1) > 1 ? `· ${i.email_sent_count}×` : ""}
                          </span>
                        ) : (
                          <span className="text-[var(--ll-ink-muted)]">Queued…</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => resendInvite(i.id)}
                            className="text-xs px-2.5 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1"
                            style={{ borderColor: "var(--ll-border)" }}
                            data-testid={`resend-invite-${i.id}`}
                            title="Re-send invite email"
                          >
                            <Send size={12} /> Resend
                          </button>
                          <button
                            onClick={() => copyInvite(i.token)}
                            className="text-xs px-2.5 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1"
                            style={{ borderColor: "var(--ll-border)" }}
                            data-testid={`copy-invite-${i.id}`}
                          >
                            <Copy size={12} /> Copy link
                          </button>
                          <button
                            onClick={() => revokeInvite(i.id)}
                            className="p-1.5 hover:bg-[var(--ll-error)] hover:text-white"
                            data-testid={`revoke-invite-${i.id}`}
                            title="Revoke"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Add-user modal */}
        {open && (
          <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[var(--ll-ink)] w-full max-w-md p-6" data-testid="add-user-form">
              <h2 className="font-display font-bold text-2xl mb-5">Add team member</h2>
              {["name", "email", "password"].map((k) => (
                <div key={k} className="mb-3">
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">{k}</label>
                  <input
                    required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    type={k === "password" ? "password" : k === "email" ? "email" : "text"}
                    data-testid={`add-user-${k}`}
                    className="w-full px-3 py-2 border" style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
              ))}
              <div className="mb-3">
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border bg-white" style={{ borderColor: "var(--ll-border)" }} data-testid="add-user-role">
                  {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 mt-5">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border" style={{ borderColor: "var(--ll-border)" }} data-testid="add-user-cancel">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[var(--ll-ink)] text-white" data-testid="add-user-submit">Add user</button>
              </div>
            </form>
          </div>
        )}

        {/* Invite-link modal */}
        {inviteOpen && (
          <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setInviteOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[var(--ll-ink)] w-full max-w-lg p-6" data-testid="invite-modal">
              <h2 className="font-display font-bold text-2xl mb-1 flex items-center gap-2"><Send size={20} /> Invite teammate by email</h2>
              <p className="text-sm text-[var(--ll-ink-soft)] mb-5">We&apos;ll email your teammate a secure one-time setup link. You can also copy the link to share manually.</p>
              <form onSubmit={sendInvite} className="space-y-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Name</label>
                  <input required value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} className="w-full px-3 py-2 border" style={{ borderColor: "var(--ll-border)" }} data-testid="invite-name" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Email</label>
                  <input required type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} className="w-full px-3 py-2 border" style={{ borderColor: "var(--ll-border)" }} data-testid="invite-email" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Role</label>
                  <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })} className="w-full px-3 py-2 border bg-white" style={{ borderColor: "var(--ll-border)" }} data-testid="invite-role">
                    {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label} — {r.desc}</option>)}
                  </select>
                </div>
                {lastInviteLink && (
                  <div className="border p-3 bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-ink)" }} data-testid="invite-link-result">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] mb-2">Shareable link</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono-fin truncate bg-white px-2 py-1.5 border" style={{ borderColor: "var(--ll-border)" }}>{lastInviteLink}</code>
                      <button
                        type="button"
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(lastInviteLink); toast.success("Copied"); } catch { toast.success("Copy from box above"); }
                        }}
                        className="text-sm px-3 py-1.5 bg-[var(--ll-ink)] text-white flex items-center gap-1.5"
                        data-testid="copy-last-invite-btn"
                      >
                        <Copy size={13} /> Copy
                      </button>
                    </div>
                    <div className="text-[11px] text-[var(--ll-ink-soft)] mt-2">Send this to your teammate. They&apos;ll set a password and land in your workspace.</div>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setInviteOpen(false)} className="px-4 py-2 border" style={{ borderColor: "var(--ll-border)" }} data-testid="invite-close">Close</button>
                  <button type="submit" className="px-4 py-2 bg-[var(--ll-ink)] text-white flex items-center gap-1.5" data-testid="invite-generate-btn">
                    <Mail size={14} /> Send invite
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </>)}
      </PageBody>

      {/* View / Edit user drawer */}
      <Dialog open={!!drawerUser} onOpenChange={(o) => !o && setDrawerUser(null)}>
        <DialogContent className="max-w-md" data-testid="user-drawer">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {drawerMode === "edit" ? <Pencil size={16} /> : <Eye size={16} />}
              {drawerMode === "edit" ? "Edit user" : "User profile"}
            </DialogTitle>
            <DialogDescription className="text-[var(--ll-ink-soft)]">
              {drawerUser?.email}
            </DialogDescription>
          </DialogHeader>

          {drawerUser && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-3 pb-4 border-b" style={{ borderColor: "var(--ll-border)" }}>
                <Avatar user={drawerUser} size={56} />
                <div>
                  <div className="font-display font-bold text-lg leading-tight">{drawerUser.name}</div>
                  <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-muted)]">{drawerUser.role}</div>
                </div>
              </div>

              {drawerMode === "view" ? (
                <>
                  <Field k="Name">{drawerUser.name}</Field>
                  <Field k="Email">{drawerUser.email}</Field>
                  <Field k="Role">{drawerUser.role}</Field>
                  <Field k="Status">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 inline-flex items-center gap-1 ${drawerUser.is_active ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${drawerUser.is_active ? "bg-[#10B981]" : "bg-[#DC2626]"}`} />
                      {drawerUser.is_active ? "Active" : "Inactive"}
                    </span>
                  </Field>
                  <Field k="Joined">{fmtDate(drawerUser.created_at)}</Field>
                  <Field k="Last seen">{fmtRelative(drawerUser.last_login_at)}</Field>
                  <Field k="Training">{certs[drawerUser.id]?.length ? <CertBadges certs={certs[drawerUser.id]} userId={`drawer-${drawerUser.id}`} downloadFor={canManage || drawerUser.id === me?.id ? drawerUser.id : null} name={drawerUser.name} /> : <span className="text-[var(--ll-ink-muted)]">No badges yet</span>}</Field>
                  {drawerUser.role === "client" && (
                    <Field k="Uploads to">
                      {(() => {
                        const b = bankAccounts.find((x) => x.id === drawerUser.default_bank_account_id);
                        return b
                          ? <span data-testid="drawer-default-bank">{b.name} · {b.bank_name}</span>
                          : <span className="text-[var(--ll-ink-muted)]" data-testid="drawer-default-bank-any">Any (client picks per upload)</span>;
                      })()}
                    </Field>
                  )}
                  {canManage && drawerUser.role !== "owner" && (
                    <button onClick={() => { setDrawerMode("edit"); setEditForm({ name: drawerUser.name, role: drawerUser.role, default_bank_account_id: drawerUser.default_bank_account_id || "" }); }} className="w-full px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center justify-center gap-2 text-sm" data-testid="drawer-edit-btn">
                      <Pencil size={14} /> Edit user
                    </button>
                  )}
                </>
              ) : (
                <>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Name</span>
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      data-testid="drawer-name-input"
                      className="mt-1 w-full px-3 py-2 border text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Role</span>
                    <select
                      value={editForm.role}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      data-testid="drawer-role-select"
                      className="mt-1 w-full px-3 py-2 border text-sm bg-white"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                    </select>
                  </label>
                  {editForm.role === "client" && (
                    <label className="block" data-testid="drawer-default-bank-field">
                      <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Default bank account</span>
                      <select
                        value={editForm.default_bank_account_id}
                        onChange={(e) => setEditForm({ ...editForm, default_bank_account_id: e.target.value })}
                        data-testid="drawer-default-bank-select"
                        className="mt-1 w-full px-3 py-2 border text-sm bg-white"
                        style={{ borderColor: "var(--ll-border)" }}
                      >
                        <option value="">Any — client picks per upload</option>
                        {bankAccounts.map((b) => (
                          <option key={b.id} value={b.id}>{b.name} · {b.bank_name}</option>
                        ))}
                      </select>
                      <span className="mt-1 block text-[11px] text-[var(--ll-ink-soft)]">
                        Every upload from this client will auto-route to the selected account, ignoring their portal picker.
                      </span>
                    </label>
                  )}
                  <div className="flex items-center gap-2 pt-2">
                    <button onClick={() => setDrawerMode("view")} className="flex-1 px-4 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="drawer-cancel-btn">Cancel</button>
                    <button onClick={saveEdit} className="flex-1 px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center justify-center gap-2 text-sm" data-testid="drawer-save-btn">
                      <Save size={14} /> Save changes
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reclaim unused team-member slot prompt */}
      <Dialog open={!!reclaimOffer} onOpenChange={(o) => !o && setReclaimOffer(null)}>
        <DialogContent className="max-w-sm" data-testid="reclaim-slot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Drop an empty seat?</DialogTitle>
            <DialogDescription className="text-[var(--ll-ink-soft)]">
              You now have {reclaimOffer?.unused || 0} paid team-member seat{(reclaimOffer?.unused || 0) === 1 ? "" : "s"} nothing is using.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 text-sm text-[var(--ll-ink)]">
            Reclaim one slot to save <strong>${Number(reclaimOffer?.monthly_savings_per_slot || 0).toFixed(2)}/mo</strong>. You can buy it back later from Settings → Workspace.
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setReclaimOffer(null)}
              data-testid="reclaim-dialog-dismiss"
              className="flex-1 px-4 py-2 border text-sm"
              style={{ borderColor: "var(--ll-border)" }}
            >
              Keep seat
            </button>
            <button
              type="button"
              onClick={confirmReclaim}
              data-testid="reclaim-dialog-confirm"
              className="flex-1 px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm"
            >
              Drop 1 seat
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ k, children }) {
  return (
    <div className="grid grid-cols-[100px,1fr] gap-3 items-center border-b py-2" style={{ borderColor: "var(--ll-border)" }}>
      <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{k}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}
